/**
 * BookingSessionContext
 * 
 * Manages booking session state for authenticated truck drivers.
 * Provides session token management, dashboard data, and authentication utilities.
 */

import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getCustomerContinuityAuth } from '../utils/customerContinuityAuth';
import { getFeatureFlag } from '../config/featureFlags';
import {
  getCustomerProfile,
  updateCustomerProfile as updateCustomerProfileApi,
  createCustomerVehicle,
  patchCustomerVehicle,
  deleteCustomerVehicle as deleteCustomerVehicleApi,
  createCustomerDriver,
  patchCustomerDriver,
  deleteCustomerDriver as deleteCustomerDriverApi,
  getMonthlySubscription as getMonthlySubscriptionApi,
  enrollMonthlySubscription as enrollMonthlySubscriptionApi,
  updateMonthlyPaymentMethod as updateMonthlyPaymentMethodApi,
  removeMonthlyPaymentMethod as removeMonthlyPaymentMethodApi,
  cancelMonthlySubscription as cancelMonthlySubscriptionApi,
  reduceMonthlySpots as reduceMonthlySpotApi,
  assignMonthlySubscriptionVehicle as assignMonthlySubscriptionVehicleApi,
  assignLotVehicle as assignLotVehicleApi,
  requestMonthlySpot as requestMonthlySpotApi,
  verifyMonthlySubscriptionVehicle as verifyMonthlySubscriptionVehicleApi
} from '../services/customerPortalApi';

export const BookingSessionContext = createContext();

// Session storage keys
const SESSION_TOKEN_KEY = 'bookingSessionToken';
const SESSION_EXPIRY_KEY = 'bookingSessionExpiry';
const continuityEnabled = process.env.REACT_APP_TPH_CONTINUITY_CHECKOUT === 'true';

// API base URL
const getApiBaseUrl = () => {
  if (continuityEnabled) return '';
  return process.env.REACT_APP_API_URL || '';
};

/**
 * BookingSessionProvider
 * 
 * Provides session management for the Magic Link authentication flow.
 * Handles session validation, dashboard data fetching, and logout.
 */
export const BookingSessionProvider = ({ children }) => {
  // Session state
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState(null);

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);

  // Profile + monthly subscription state
  const [profileData, setProfileData] = useState(null);
  const [monthlySubscriptionData, setMonthlySubscriptionData] = useState(null);
  const [monthlySubscriptionsData, setMonthlySubscriptionsData] = useState([]);
  const [monthlyAccountSubscriptionData, setMonthlyAccountSubscriptionData] = useState(null);
  const [monthlyEnrollmentRequired, setMonthlyEnrollmentRequired] = useState(false);
  const [monthlyOfferEligibility, setMonthlyOfferEligibility] = useState(null);
  const [monthlyPaymentMethod, setMonthlyPaymentMethod] = useState(null);
  const [monthlyPaymentMethodsData, setMonthlyPaymentMethodsData] = useState([]);
  const [monthlyBillingEventsData, setMonthlyBillingEventsData] = useState([]);
  const [portalError, setPortalError] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isMonthlyLoading, setIsMonthlyLoading] = useState(false);

  // Prevent duplicate operations in StrictMode
  const initializingRef = useRef(false);
  const validatingRef = useRef(false);
  const monthlyPortalInitSessionRef = useRef(null);

  /**
   * Save session to storage
   */
  const saveSession = useCallback((token, expiresAt) => {
    try {
      localStorage.setItem(SESSION_TOKEN_KEY, token);
      localStorage.setItem(SESSION_EXPIRY_KEY, expiresAt);
      setSessionToken(token);
      setSessionExpiry(expiresAt);
      setIsAuthenticated(true);
      // Force dashboard/profile refresh for new auth context so newly-created
      // bookings are not hidden behind stale in-memory data.
      setDashboardData(null);
      setDashboardError(null);
      setProfileData(null);
      setMonthlySubscriptionData(null);
      setMonthlySubscriptionsData([]);
      setMonthlyAccountSubscriptionData(null);
      setMonthlyEnrollmentRequired(false);
      setMonthlyOfferEligibility(null);
      setMonthlyPaymentMethod(null);
      setMonthlyPaymentMethodsData([]);
      setMonthlyBillingEventsData([]);
      setPortalError(null);
    } catch (error) {
      console.error('Failed to save session:', error);
    }
  }, []);

  /**
   * Clear session from storage
   */
  const clearSession = useCallback(() => {
    try {
      localStorage.removeItem(SESSION_TOKEN_KEY);
      localStorage.removeItem(SESSION_EXPIRY_KEY);
      setSessionToken(null);
      setSessionExpiry(null);
      setIsAuthenticated(false);
      setAuthMethod(null);
      setDashboardData(null);
      setDashboardError(null);
      setProfileData(null);
      setMonthlySubscriptionData(null);
      setMonthlySubscriptionsData([]);
      setMonthlyAccountSubscriptionData(null);
      setMonthlyEnrollmentRequired(false);
      setMonthlyOfferEligibility(null);
      setMonthlyPaymentMethod(null);
      setMonthlyPaymentMethodsData([]);
      setMonthlyBillingEventsData([]);
      setPortalError(null);
    } catch (error) {
      console.error('Failed to clear session:', error);
    }
  }, []);

  /**
   * Validate session token with backend
   */
  const validateSession = useCallback(async (token) => {
    if (validatingRef.current) return { valid: false };
    validatingRef.current = true;

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/bookings/auth/session`, {
        ...(continuityEnabled ? { referrerPolicy: 'origin', credentials: 'same-origin', cache: 'no-store', redirect: 'error' } : {}),
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success && data.valid) {
        setAuthMethod(data.authMethod);
        return { valid: true, expiresAt: data.expiresAt, authMethod: data.authMethod };
      }

      return { valid: false };
    } catch (error) {
      console.error('Session validation error:', error);
      return { valid: false };
    } finally {
      validatingRef.current = false;
    }
  }, []);

  /**
   * Initialize session from storage on mount
   */
  useEffect(() => {
    const initializeSession = async () => {
      if (initializingRef.current) return;
      initializingRef.current = true;

      try {
        const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);
        const storedExpiry = localStorage.getItem(SESSION_EXPIRY_KEY);

        if (!storedToken) {
          setIsLoading(false);
          return;
        }

        // Check if session is expired locally first
        if (storedExpiry && new Date(storedExpiry) < new Date()) {
          clearSession();
          setIsLoading(false);
          return;
        }

        // Validate with backend
        const result = await validateSession(storedToken);

        if (result.valid) {
          setSessionToken(storedToken);
          setSessionExpiry(result.expiresAt || storedExpiry);
          setIsAuthenticated(true);
          setAuthMethod(result.authMethod);
        } else {
          clearSession();
        }
      } catch (error) {
        console.error('Session initialization error:', error);
        clearSession();
      } finally {
        setIsLoading(false);
        initializingRef.current = false;
      }
    };

    initializeSession();
  }, [validateSession, clearSession]);

  /**
   * Authenticate with magic link code
   */
  const authenticateWithMagicLink = useCallback(async (code) => {
    setIsLoading(true);
    if (continuityEnabled) {
      try { const data = await getCustomerContinuityAuth().verifyMagic(code); saveSession(data.sessionToken, data.expiresAt); setAuthMethod(data.authMethod); return { success: true, linkSource: 'continuity_full' }; }
      catch (error) { return { success: false, pending: Boolean(error.pending), error: error.code || 'auth_pending', message: error.message }; }
      finally { setIsLoading(false); }
    }
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/bookings/auth/magic-link/validate/${code}`, {
        ...(continuityEnabled ? { referrerPolicy: 'origin', credentials: 'same-origin', cache: 'no-store', redirect: 'error' } : {}),
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        saveSession(data.sessionToken, data.expiresAt);
        setAuthMethod('magic_link');
        return { 
          success: true, 
          bookingId: data.bookingId,
          linkSource: data.linkSource || 'unknown'
        };
      }

      return { 
        success: false, 
        error: data.error, 
        message: data.message 
      };
    } catch (error) {
      console.error('Magic link authentication error:', error);
      return { 
        success: false, 
        error: 'network_error', 
        message: 'Unable to connect. Please check your internet connection.' 
      };
    } finally {
      setIsLoading(false);
    }
  }, [saveSession]);

  /**
   * Request a new magic link
   * Backward-compatible signatures:
   *   requestMagicLink(phone, bookingId?, deliveryMethod?)
   *   requestMagicLink({ phone?, email?, bookingId?, deliveryMethod? })
   */
  const requestMagicLink = useCallback(async (contactOrPayload, bookingId = null, deliveryMethod = 'both') => {
    const payload = (contactOrPayload && typeof contactOrPayload === 'object' && !Array.isArray(contactOrPayload))
      ? contactOrPayload
      : { phone: contactOrPayload, bookingId, deliveryMethod };

    if (continuityEnabled) {
      if (!payload.phone || payload.email || payload.bookingId || (payload.deliveryMethod && payload.deliveryMethod !== 'sms')) return { success: false, error: 'auth_scope_unsupported', message: 'Use phone sign-in for your full customer account.' };
      try { return { ...await getCustomerContinuityAuth().request({ phone: payload.phone, channel: 'magic-link' }), success: true, message: 'Check your phone for your sign-in link.' }; }
      catch (error) { return { success: false, pending: Boolean(error.pending), error: error.code || 'auth_pending', message: error.message }; }
    }
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/bookings/auth/magic-link/request`, {
        ...(continuityEnabled ? { referrerPolicy: 'origin', credentials: 'same-origin', cache: 'no-store', redirect: 'error' } : {}),
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Magic link request error:', error);
      return { 
        success: false, 
        error: 'network_error', 
        message: 'Unable to connect. Please try again.' 
      };
    }
  }, []);

  /**
   * Authenticate with fallback method (phone last 4 + booking ID)
   */
  const authenticateWithFallback = useCallback(async (bookingId, phoneLast4) => {
    if (continuityEnabled) return { success: false, error: 'auth_scope_unsupported' };
    setIsLoading(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/bookings/auth/fallback`, {
        ...(continuityEnabled ? { referrerPolicy: 'origin', credentials: 'same-origin', cache: 'no-store', redirect: 'error' } : {}),
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bookingId, phoneLast4 })
      });

      const data = await response.json();

      if (data.success) {
        saveSession(data.sessionToken, data.expiresAt);
        setAuthMethod('fallback');
        return { 
          success: true, 
          bookingId: data.bookingId 
        };
      }

      return { 
        success: false, 
        error: data.error, 
        message: data.message 
      };
    } catch (error) {
      console.error('Fallback authentication error:', error);
      return { 
        success: false, 
        error: 'network_error', 
        message: 'Unable to connect. Please try again.' 
      };
    } finally {
      setIsLoading(false);
    }
  }, [saveSession]);

  /**
   * Upgrade OTP session to Magic Link session
   * Called after successful OTP verification to create a persistent session
   */
  const upgradeOtpSession = useCallback(async (bookingId) => {
    if (continuityEnabled) return { success: false, error: 'auth_scope_unsupported' };
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/bookings/auth/upgrade-session`, {
        ...(continuityEnabled ? { referrerPolicy: 'origin', credentials: 'same-origin', cache: 'no-store', redirect: 'error' } : {}),
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bookingId })
      });

      const data = await response.json();

      if (data.success) {
        saveSession(data.sessionToken, data.expiresAt);
        setAuthMethod('otp');
        return { success: true };
      }

      return { 
        success: false, 
        error: data.error 
      };
    } catch (error) {
      console.error('Session upgrade error:', error);
      return { 
        success: false, 
        error: 'network_error' 
      };
    }
  }, [saveSession]);

  const requestCustomerSignIn = useCallback(async (phone, channel = 'otp', startNew = false) => {
    if (!continuityEnabled) return { success: false, error: 'auth_scope_unsupported' };
    try { return { ...await getCustomerContinuityAuth().request({ phone, channel, startNew }), success: true }; }
    catch (error) { return { success: false, pending: Boolean(error.pending), error: error.code || 'auth_pending', message: error.message }; }
  }, []);
  const verifyCustomerOtp = useCallback(async (code) => {
    if (!continuityEnabled) return { success: false, error: 'auth_scope_unsupported' };
    try { const data = await getCustomerContinuityAuth().verifyOtp(code); saveSession(data.sessionToken, data.expiresAt); setAuthMethod(data.authMethod); return { success: true }; }
    catch (error) { return { success: false, pending: Boolean(error.pending), error: error.code || 'auth_pending', message: error.message }; }
  }, [saveSession]);

  /**
   * Fetch dashboard data for authenticated user
   */
  const fetchDashboard = useCallback(async () => {
    if (!sessionToken) {
      setDashboardError('Not authenticated');
      return { success: false, error: 'not_authenticated' };
    }

    setIsDashboardLoading(true);
    setDashboardError(null);

    try {
      const url = `${getApiBaseUrl()}/api/bookings/dashboard`;
      const response = await fetch(url, {
        ...(continuityEnabled ? { referrerPolicy: 'origin', credentials: 'same-origin', cache: 'no-store', redirect: 'error' } : {}),
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setDashboardData(data);
        return { success: true, data };
      }

      // Handle authentication errors
      if (response.status === 401) {
        clearSession();
        setDashboardError('Session expired. Please sign in again.');
        return { success: false, error: 'session_expired' };
      }

      setDashboardError(data.message || 'Failed to load dashboard');
      return { success: false, error: data.error };
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      setDashboardError('Unable to load your bookings. Please try again.');
      return { success: false, error: 'network_error' };
    } finally {
      setIsDashboardLoading(false);
    }
  }, [sessionToken, clearSession]);

  /**
   * Fetch customer profile from customer portal API
   */
  const fetchProfile = useCallback(async () => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsProfileLoading(true);
    setPortalError(null);
    try {
      const profile = await getCustomerProfile(sessionToken);
      setProfileData(profile);
      return { success: true, profile };
    } catch (error) {
      setPortalError(error.message || 'Failed to load profile');
      return { success: false, error: error.message || 'profile_error' };
    } finally {
      setIsProfileLoading(false);
    }
  }, [sessionToken]);

  const updateProfile = useCallback(async (updates) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsProfileLoading(true);
    setPortalError(null);
    try {
      const profile = await updateCustomerProfileApi(sessionToken, updates || {});
      setProfileData(profile);
      return { success: true, profile };
    } catch (error) {
      setPortalError(error.message || 'Failed to update profile');
      return { success: false, error: error.message || 'profile_update_error' };
    } finally {
      setIsProfileLoading(false);
    }
  }, [sessionToken]);

  const addProfileVehicle = useCallback(async (vehiclePayload) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsProfileLoading(true);
    setPortalError(null);
    try {
      const profile = await createCustomerVehicle(sessionToken, vehiclePayload || {});
      setProfileData(profile);
      return { success: true, profile };
    } catch (error) {
      setPortalError(error.message || 'Failed to add vehicle');
      return { success: false, error: error.message || 'vehicle_add_error' };
    } finally {
      setIsProfileLoading(false);
    }
  }, [sessionToken]);

  const updateProfileVehicle = useCallback(async (vehicleId, updates) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsProfileLoading(true);
    setPortalError(null);
    try {
      const profile = await patchCustomerVehicle(sessionToken, vehicleId, updates || {});
      setProfileData(profile);
      return { success: true, profile };
    } catch (error) {
      setPortalError(error.message || 'Failed to update vehicle');
      return { success: false, error: error.message || 'vehicle_update_error' };
    } finally {
      setIsProfileLoading(false);
    }
  }, [sessionToken]);

  const deleteProfileVehicle = useCallback(async (vehicleId) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsProfileLoading(true);
    setPortalError(null);
    try {
      const profile = await deleteCustomerVehicleApi(sessionToken, vehicleId);
      setProfileData(profile);
      return { success: true, profile };
    } catch (error) {
      setPortalError(error.message || 'Failed to delete vehicle');
      return { success: false, error: error.message || 'vehicle_delete_error' };
    } finally {
      setIsProfileLoading(false);
    }
  }, [sessionToken]);

  const addProfileDriver = useCallback(async (driverPayload) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsProfileLoading(true);
    setPortalError(null);
    try {
      const profile = await createCustomerDriver(sessionToken, driverPayload || {});
      setProfileData(profile);
      return { success: true, profile };
    } catch (error) {
      setPortalError(error.message || 'Failed to add driver');
      return { success: false, error: error.message || 'driver_add_error' };
    } finally {
      setIsProfileLoading(false);
    }
  }, [sessionToken]);

  const updateProfileDriver = useCallback(async (driverId, updates) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsProfileLoading(true);
    setPortalError(null);
    try {
      const profile = await patchCustomerDriver(sessionToken, driverId, updates || {});
      setProfileData(profile);
      return { success: true, profile };
    } catch (error) {
      setPortalError(error.message || 'Failed to update driver');
      return { success: false, error: error.message || 'driver_update_error' };
    } finally {
      setIsProfileLoading(false);
    }
  }, [sessionToken]);

  const deleteProfileDriver = useCallback(async (driverId) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsProfileLoading(true);
    setPortalError(null);
    try {
      const profile = await deleteCustomerDriverApi(sessionToken, driverId);
      setProfileData(profile);
      return { success: true, profile };
    } catch (error) {
      setPortalError(error.message || 'Failed to delete driver');
      return { success: false, error: error.message || 'driver_delete_error' };
    } finally {
      setIsProfileLoading(false);
    }
  }, [sessionToken]);

  const fetchMonthlySubscription = useCallback(async () => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsMonthlyLoading(true);
    setPortalError(null);
    try {
      const snapshot = await getMonthlySubscriptionApi(sessionToken);
      if (snapshot.profile) {
        setProfileData(snapshot.profile);
      }
      const subscriptions = Array.isArray(snapshot.subscriptions) ? snapshot.subscriptions : [];
      setMonthlySubscriptionsData(subscriptions);
      setMonthlySubscriptionData(snapshot.subscription || subscriptions[0] || null);
      setMonthlyAccountSubscriptionData(snapshot.accountSubscription || null);
      setMonthlyEnrollmentRequired(Boolean(snapshot.enrollmentRequired));
      setMonthlyOfferEligibility(snapshot.offerEligibility || null);
      setMonthlyPaymentMethod(snapshot.paymentMethod || null);
      setMonthlyPaymentMethodsData(Array.isArray(snapshot.paymentMethods) ? snapshot.paymentMethods : []);
      setMonthlyBillingEventsData(Array.isArray(snapshot.billingEvents) ? snapshot.billingEvents : []);
      return { success: true, ...snapshot };
    } catch (error) {
      setPortalError(error.message || 'Failed to load monthly subscription');
      return { success: false, error: error.message || 'monthly_fetch_error' };
    } finally {
      setIsMonthlyLoading(false);
    }
  }, [sessionToken]);

  const enrollMonthlySubscription = useCallback(async (payload, tokenize = null) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsMonthlyLoading(true);
    setPortalError(null);
    try {
      const snapshot = await (continuityEnabled
        ? enrollMonthlySubscriptionApi(sessionToken, payload, tokenize || undefined)
        : enrollMonthlySubscriptionApi(sessionToken, payload));
      if (snapshot.profile) {
        setProfileData(snapshot.profile);
      }
      const subscriptions = Array.isArray(snapshot.subscriptions) ? snapshot.subscriptions : [];
      setMonthlySubscriptionsData(subscriptions);
      setMonthlySubscriptionData(snapshot.subscription || subscriptions[0] || null);
      setMonthlyAccountSubscriptionData(snapshot.accountSubscription || null);
      setMonthlyEnrollmentRequired(Boolean(snapshot.enrollmentRequired));
      setMonthlyOfferEligibility(snapshot.offerEligibility || null);
      setMonthlyPaymentMethod(snapshot.paymentMethod || null);
      setMonthlyPaymentMethodsData(Array.isArray(snapshot.paymentMethods) ? snapshot.paymentMethods : []);
      if (Array.isArray(snapshot.billingEvents)) {
        setMonthlyBillingEventsData(snapshot.billingEvents);
      }
      return { success: true, ...snapshot };
    } catch (error) {
      setPortalError(error.message || 'Failed to enroll monthly subscription');
      return { success: false, error: error.message || 'monthly_enroll_error',
        ...(continuityEnabled ? { pending: Boolean(error.pending), status: error.status || null, code: error.code || null } : {}) };
    } finally {
      setIsMonthlyLoading(false);
    }
  }, [sessionToken]);

  const updateMonthlyPaymentMethod = useCallback(async (sourceId = null, subscriptionId = null, paymentMethodId = null, tokenize = null) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsMonthlyLoading(true);
    setPortalError(null);
    try {
      const snapshot = await (continuityEnabled ? updateMonthlyPaymentMethodApi(
        sessionToken,
        sourceId || undefined,
        paymentMethodId || undefined,
        subscriptionId || undefined,
        undefined,
        tokenize || undefined
      ) : updateMonthlyPaymentMethodApi(sessionToken, sourceId || undefined, paymentMethodId || undefined, subscriptionId || undefined));
      if (snapshot.profile) {
        setProfileData(snapshot.profile);
      }
      const subscriptions = Array.isArray(snapshot.subscriptions) ? snapshot.subscriptions : [];
      setMonthlySubscriptionsData(subscriptions);
      setMonthlySubscriptionData(snapshot.subscription || subscriptions[0] || null);
      setMonthlyAccountSubscriptionData(snapshot.accountSubscription || null);
      setMonthlyEnrollmentRequired(Boolean(snapshot.enrollmentRequired));
      setMonthlyPaymentMethod(snapshot.paymentMethod || null);
      setMonthlyPaymentMethodsData(Array.isArray(snapshot.paymentMethods) ? snapshot.paymentMethods : []);
      if (Array.isArray(snapshot.billingEvents)) {
        setMonthlyBillingEventsData(snapshot.billingEvents);
      }
      await fetchMonthlySubscription();
      return { success: true, ...snapshot };
    } catch (error) {
      setPortalError(error.message || 'Failed to update payment method');
      return { success: false, error: error.message || 'monthly_payment_method_error',
        ...(continuityEnabled ? { pending: Boolean(error.pending), status: error.status || null, code: error.code || null } : {}) };
    } finally {
      setIsMonthlyLoading(false);
    }
  }, [sessionToken, fetchMonthlySubscription]);

  const removeMonthlyPaymentMethod = useCallback(async (paymentMethodId) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsMonthlyLoading(true);
    setPortalError(null);
    try {
      const snapshot = await removeMonthlyPaymentMethodApi(sessionToken, paymentMethodId);
      if (snapshot.profile) {
        setProfileData(snapshot.profile);
      }
      const subscriptions = Array.isArray(snapshot.subscriptions) ? snapshot.subscriptions : [];
      setMonthlySubscriptionsData(subscriptions);
      setMonthlySubscriptionData(snapshot.subscription || subscriptions[0] || null);
      setMonthlyAccountSubscriptionData(snapshot.accountSubscription || null);
      setMonthlyEnrollmentRequired(Boolean(snapshot.enrollmentRequired));
      setMonthlyPaymentMethod(snapshot.paymentMethod || null);
      setMonthlyPaymentMethodsData(Array.isArray(snapshot.paymentMethods) ? snapshot.paymentMethods : []);
      if (Array.isArray(snapshot.billingEvents)) {
        setMonthlyBillingEventsData(snapshot.billingEvents);
      }
      await fetchMonthlySubscription();
      return { success: true, ...snapshot };
    } catch (error) {
      setPortalError(error.message || 'Failed to remove payment method');
      return { success: false, error: error.message || 'monthly_payment_method_remove_error' };
    } finally {
      setIsMonthlyLoading(false);
    }
  }, [sessionToken, fetchMonthlySubscription]);

  const cancelMonthlySubscription = useCallback(async (subscriptionId = null) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsMonthlyLoading(true);
    setPortalError(null);
    try {
      const snapshot = await cancelMonthlySubscriptionApi(sessionToken, subscriptionId || undefined);
      const subscriptions = Array.isArray(snapshot.subscriptions) ? snapshot.subscriptions : [];
      setMonthlySubscriptionsData(subscriptions);
      setMonthlySubscriptionData(snapshot.subscription || subscriptions[0] || null);
      setMonthlyAccountSubscriptionData(snapshot.accountSubscription || null);
      setMonthlyEnrollmentRequired(Boolean(snapshot.enrollmentRequired));
      setMonthlyPaymentMethodsData([]);
      await fetchMonthlySubscription();
      return {
        success: true,
        subscription: snapshot.subscription || null,
        subscriptions
      };
    } catch (error) {
      setPortalError(error.message || 'Failed to cancel subscription');
      return { success: false, error: error.message || 'monthly_cancel_error',
        ...(continuityEnabled ? { pending: Boolean(error.pending), status: error.status || null, code: error.code || null } : {}) };
    } finally {
      setIsMonthlyLoading(false);
    }
  }, [sessionToken]);

  const reduceMonthlySpots = useCallback(async (subscriptionId, spotsToRemove) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }
    setIsMonthlyLoading(true);
    setPortalError(null);
    try {
      const snapshot = await reduceMonthlySpotApi(sessionToken, { subscriptionId, spotsToRemove });
      const subscriptions = Array.isArray(snapshot.subscriptions) ? snapshot.subscriptions : [];
      setMonthlySubscriptionsData(subscriptions);
      setMonthlySubscriptionData(snapshot.subscription || subscriptions[0] || null);
      await fetchMonthlySubscription();
      return { success: true, subscription: snapshot.subscription || null, subscriptions };
    } catch (error) {
      setPortalError(error.message || 'Failed to reduce spots');
      return { success: false, error: error.message || 'monthly_reduce_spots_error',
        ...(continuityEnabled ? { pending: Boolean(error.pending), status: error.status || null, code: error.code || null } : {}) };
    } finally {
      setIsMonthlyLoading(false);
    }
  }, [sessionToken, fetchMonthlySubscription]);

  const assignMonthlySubscriptionVehicle = useCallback(async (subscriptionId, vehicleId) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsMonthlyLoading(true);
    setPortalError(null);
    try {
      const snapshot = await assignMonthlySubscriptionVehicleApi(sessionToken, {
        subscriptionId,
        vehicleId
      });
      if (snapshot.profile) {
        setProfileData(snapshot.profile);
      }
      const subscriptions = Array.isArray(snapshot.subscriptions) ? snapshot.subscriptions : [];
      setMonthlySubscriptionsData(subscriptions);
      setMonthlySubscriptionData(snapshot.subscription || subscriptions[0] || null);
      setMonthlyPaymentMethodsData([]);
      await fetchMonthlySubscription();
      return {
        success: true,
        subscription: snapshot.subscription || null,
        subscriptions
      };
    } catch (error) {
      setPortalError(error.message || 'Failed to assign truck unit');
      return { success: false, error: error.message || 'monthly_assign_vehicle_error' };
    } finally {
      setIsMonthlyLoading(false);
    }
  }, [sessionToken, fetchMonthlySubscription]);

  const assignLotVehicle = useCallback(async (subscriptionId, lotId, vehicleId) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }
    setIsMonthlyLoading(true);
    setPortalError(null);
    try {
      const snapshot = await assignLotVehicleApi(sessionToken, { subscriptionId, lotId, vehicleId: vehicleId || null });
      if (snapshot.profile) {
        setProfileData(snapshot.profile);
      }
      const subscriptions = Array.isArray(snapshot.subscriptions) ? snapshot.subscriptions : [];
      setMonthlySubscriptionsData(subscriptions);
      setMonthlySubscriptionData(snapshot.subscription || subscriptions[0] || null);
      await fetchMonthlySubscription();
      return { success: true, subscription: snapshot.subscription || null, subscriptions };
    } catch (error) {
      setPortalError(error.message || 'Failed to assign lot vehicle');
      return { success: false, error: error.message || 'monthly_assign_lot_vehicle_error' };
    } finally {
      setIsMonthlyLoading(false);
    }
  }, [sessionToken, fetchMonthlySubscription]);

  const requestMonthlySpot = useCallback(async (payload = {}, tokenize) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setIsMonthlyLoading(true);
    setPortalError(null);
    try {
      const snapshot = await (continuityEnabled ? requestMonthlySpotApi(sessionToken, payload || {}, tokenize)
        : requestMonthlySpotApi(sessionToken, payload || {}));
      if (snapshot.profile) {
        setProfileData(snapshot.profile);
      }
      const subscriptions = Array.isArray(snapshot.subscriptions) ? snapshot.subscriptions : [];
      setMonthlySubscriptionsData(subscriptions);
      setMonthlySubscriptionData(snapshot.subscription || subscriptions[0] || null);
      setMonthlyAccountSubscriptionData(snapshot.accountSubscription || null);
      setMonthlyEnrollmentRequired(Boolean(snapshot.enrollmentRequired));
      setMonthlyPaymentMethod(snapshot.paymentMethod || null);
      setMonthlyPaymentMethodsData(Array.isArray(snapshot.paymentMethods) ? snapshot.paymentMethods : []);
      await fetchMonthlySubscription();
      return {
        success: true,
        subscription: snapshot.subscription || null,
        subscriptions
      };
    } catch (error) {
      setPortalError(error.message || 'Failed to add additional monthly spot');
      return { success: false, error: error.message || 'monthly_add_spot_error',
        ...(continuityEnabled ? { pending: Boolean(error.pending), status: error.status || null, code: error.code || null } : {}) };
    } finally {
      setIsMonthlyLoading(false);
    }
  }, [sessionToken, fetchMonthlySubscription]);

  const requestAdditionalMonthlySpot = useCallback(async (subscriptionId, additionalSpots = 1) => {
    return requestMonthlySpot({
      subscriptionId,
      additionalSpots
    });
  }, [requestMonthlySpot]);

  const verifyMonthlySubscriptionVehicle = useCallback(async (subscriptionId, unitType, unitNumber) => {
    if (!sessionToken) {
      return { success: false, error: 'not_authenticated' };
    }

    setPortalError(null);
    try {
      const result = await verifyMonthlySubscriptionVehicleApi(sessionToken, {
        subscriptionId,
        unitType,
        unitNumber
      });
      return { success: true, ...result };
    } catch (error) {
      setPortalError(error.message || 'Failed to verify vehicle ownership');
      return { success: false, error: error.message || 'monthly_vehicle_verify_error' };
    }
  }, [sessionToken]);

  /**
   * Logout and invalidate session
   */
  const logout = useCallback(async () => {
    if (!sessionToken) {
      clearSession();
      return;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/bookings/auth/session`, {
        ...(continuityEnabled ? { credentials: 'same-origin', cache: 'no-store', redirect: 'error', referrerPolicy: 'origin' } : {}),
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (continuityEnabled) {
        const result = await response.json();
        if (!response.ok || result.success !== true) throw new Error('Server sign-out could not be confirmed. Please try again.');
        clearSession();
        return { success: true };
      }
    } catch (error) {
      console.error('Logout error:', error);
      if (continuityEnabled) {
        const message = 'Server sign-out could not be confirmed. Please try again.';
        setPortalError(message);
        return { success: false, error: message };
      }
    } finally {
      if (!continuityEnabled) clearSession();
    }
  }, [sessionToken, clearSession]);

  /**
   * Get authorization header for API calls
   */
  const getAuthHeaders = useCallback(() => {
    if (!sessionToken) return {};
    return {
      'Authorization': `Bearer ${sessionToken}`
    };
  }, [sessionToken]);

  useEffect(() => {
    if (continuityEnabled && isAuthenticated && sessionToken) fetchDashboard();
  }, [isAuthenticated, sessionToken, fetchDashboard]);

  /**
   * Check if monthly subscription customer portal is enabled
   */
  const isMonthlySubscriptionsEnabled = useMemo(() => {
    try {
      return Boolean(getFeatureFlag('enableMonthlySubscriptions'));
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !sessionToken || !isMonthlySubscriptionsEnabled) {
      monthlyPortalInitSessionRef.current = null;
      return;
    }

    if (monthlyPortalInitSessionRef.current === sessionToken) {
      return;
    }

    monthlyPortalInitSessionRef.current = sessionToken;
    fetchProfile();
    fetchMonthlySubscription();
  }, [
    isAuthenticated,
    sessionToken,
    isMonthlySubscriptionsEnabled,
    fetchProfile,
    fetchMonthlySubscription
  ]);

  /**
   * Check if Magic Link auth is enabled
   */
  const isMagicLinkEnabled = useMemo(() => {
    try {
      const flagValue = getFeatureFlag('bookingAuthMethod');
      return flagValue === 'magic_link' || flagValue === 'both';
    } catch {
      return false;
    }
  }, []);

  /**
   * Check if OTP auth is enabled
   */
  const isOtpEnabled = useMemo(() => {
    try {
      const flagValue = getFeatureFlag('bookingAuthMethod');
      return flagValue === 'otp' || flagValue === 'both';
    } catch {
      return true; // Default to OTP
    }
  }, []);

  /**
   * Get the booking auth method setting
   */
  const bookingAuthMethod = useMemo(() => {
    try {
      return getFeatureFlag('bookingAuthMethod') || 'otp';
    } catch {
      return 'otp';
    }
  }, []);

  // Memoized context value
  const contextValue = useMemo(() => ({
    // Session state
    sessionToken,
    sessionExpiry,
    isAuthenticated,
    isLoading,
    authMethod,

    // Dashboard state
    dashboardData,
    isDashboardLoading,
    dashboardError,
    profileData,
    monthlySubscriptionData,
    monthlySubscriptionsData,
    monthlyAccountSubscriptionData,
    monthlyEnrollmentRequired,
    monthlyOfferEligibility,
    monthlyPaymentMethod,
    monthlyPaymentMethodsData,
    monthlyBillingEventsData,
    portalError,
    isProfileLoading,
    isMonthlyLoading,

    // Feature flags
    isMagicLinkEnabled,
    isOtpEnabled,
    bookingAuthMethod,
    isMonthlySubscriptionsEnabled,

    // Actions
    saveSession,
    clearSession,
    authenticateWithMagicLink,
    requestCustomerSignIn,
    verifyCustomerOtp,
    requestMagicLink,
    authenticateWithFallback,
    upgradeOtpSession,
    fetchDashboard,
    fetchProfile,
    updateProfile,
    addProfileVehicle,
    updateProfileVehicle,
    deleteProfileVehicle,
    addProfileDriver,
    updateProfileDriver,
    deleteProfileDriver,
    fetchMonthlySubscription,
    enrollMonthlySubscription,
    updateMonthlyPaymentMethod,
    removeMonthlyPaymentMethod,
    cancelMonthlySubscription,
    reduceMonthlySpots,
    assignMonthlySubscriptionVehicle,
    assignLotVehicle,
    requestMonthlySpot,
    requestAdditionalMonthlySpot,
    verifyMonthlySubscriptionVehicle,
    logout,
    getAuthHeaders,
    validateSession
  }), [
    sessionToken,
    sessionExpiry,
    isAuthenticated,
    isLoading,
    authMethod,
    dashboardData,
    isDashboardLoading,
    dashboardError,
    profileData,
    monthlySubscriptionData,
    monthlySubscriptionsData,
    monthlyAccountSubscriptionData,
    monthlyEnrollmentRequired,
    monthlyOfferEligibility,
    monthlyPaymentMethod,
    monthlyPaymentMethodsData,
    monthlyBillingEventsData,
    portalError,
    isProfileLoading,
    isMonthlyLoading,
    isMagicLinkEnabled,
    isOtpEnabled,
    bookingAuthMethod,
    isMonthlySubscriptionsEnabled,
    saveSession,
    clearSession,
    authenticateWithMagicLink,
    requestCustomerSignIn,
    verifyCustomerOtp,
    requestMagicLink,
    authenticateWithFallback,
    upgradeOtpSession,
    fetchDashboard,
    fetchProfile,
    updateProfile,
    addProfileVehicle,
    updateProfileVehicle,
    deleteProfileVehicle,
    addProfileDriver,
    updateProfileDriver,
    deleteProfileDriver,
    fetchMonthlySubscription,
    enrollMonthlySubscription,
    updateMonthlyPaymentMethod,
    removeMonthlyPaymentMethod,
    cancelMonthlySubscription,
    reduceMonthlySpots,
    assignMonthlySubscriptionVehicle,
    assignLotVehicle,
    requestMonthlySpot,
    requestAdditionalMonthlySpot,
    verifyMonthlySubscriptionVehicle,
    logout,
    getAuthHeaders,
    validateSession
  ]);

  return (
    <BookingSessionContext.Provider value={contextValue}>
      {children}
    </BookingSessionContext.Provider>
  );
};

/**
 * useBookingSession hook
 * 
 * Provides easy access to booking session context.
 * Must be used within a BookingSessionProvider.
 */
export const useBookingSession = () => {
  const context = React.useContext(BookingSessionContext);
  
  if (!context) {
    throw new Error('useBookingSession must be used within a BookingSessionProvider');
  }
  
  return context;
};

export default BookingSessionContext;
